import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  FiArrowRight,
  FiArrowUpRight,
  FiDownload,
  FiGithub,
  FiLinkedin,
  FiExternalLink,
  FiMail,
  FiCode,
  FiTerminal,
  FiCpu,
  FiZap,
  FiBox,
} from "react-icons/fi";
import {
  SiPython,
  SiJavascript,
  SiTypescript,
  SiReact,
  SiNodedotjs,
  SiMongodb,
  SiMysql,
  SiHtml5,
  SiCss3,
  SiCplusplus,
  SiOpenjdk,
  SiC,
  SiGit,
  SiGithub,
  SiPhp,
  SiTailwindcss,
  SiAmazonwebservices,
  SiDocker,
  SiLinux,
  SiKubernetes,
  SiJenkins,
  SiNginx,
  SiPostman,
  SiRedis,
  SiFirebase,
  SiVercel,
  SiNetlify,
  SiFigma,
  SiBootstrap,
  SiExpress,
  SiNextdotjs,
  SiGraphql,
  SiPostgresql,
  SiGo,
  SiRust,
} from "react-icons/si";
import {
  profileAPI,
  projectsAPI,
  skillsAPI,
  getImageUrl,
} from "../services/api";
import LoadingSpinner from "../components/LoadingSpinner";

const skillIconsMap = {
  python: <SiPython className="text-yellow-500" />,
  javascript: <SiJavascript className="text-yellow-400" />,
  typescript: <SiTypescript className="text-blue-500" />,
  react: <SiReact className="text-cyan-500" />,
  node: <SiNodedotjs className="text-green-600" />,
  mongodb: <SiMongodb className="text-green-500" />,
  mysql: <SiMysql className="text-blue-500" />,
  html: <SiHtml5 className="text-orange-500" />,
  css: <SiCss3 className="text-blue-500" />,
  cplusplus: <SiCplusplus className="text-blue-600" />,
  java: <SiOpenjdk className="text-red-600" />,
  c: <SiC className="text-gray-600" />,
  git: <SiGit className="text-orange-600" />,
  github: <SiGithub className="text-gray-800 dark:text-white" />,
  php: <SiPhp className="text-indigo-500" />,
  tailwindcss: <SiTailwindcss className="text-blue-500 text-2xl" />,
  go: <SiGo className="text-sky-500" />,
  rust: <SiRust className="text-orange-700" />,
  aws: <SiAmazonwebservices className="text-orange-500" />,
  amazonwebservices: <SiAmazonwebservices className="text-orange-500" />,
  docker: <SiDocker className="text-sky-500" />,
  nodejs: <SiNodedotjs className="text-green-600" />,
  nodejsruntime: <SiNodedotjs className="text-green-600" />,
  linux: <SiLinux className="text-yellow-500" />,
  kubernetes: <SiKubernetes className="text-blue-600" />,
  jenkins: <SiJenkins className="text-red-500" />,
  nginx: <SiNginx className="text-green-600" />,
  postman: <SiPostman className="text-orange-500" />,
  redis: <SiRedis className="text-red-600" />,
  firebase: <SiFirebase className="text-amber-500" />,
  vercel: <SiVercel className="text-black dark:text-white" />,
  netlify: <SiNetlify className="text-teal-500" />,
  figma: <SiFigma className="text-pink-500" />,
  bootstrap: <SiBootstrap className="text-purple-600" />,
  express: <SiExpress className="text-gray-800 dark:text-white" />,
  nextjs: <SiNextdotjs className="text-black dark:text-white" />,
  nextdotjs: <SiNextdotjs className="text-black dark:text-white" />,
  graphql: <SiGraphql className="text-pink-600" />,
  postgresql: <SiPostgresql className="text-blue-700" />,
  vscode: <FiCode className="text-blue-600" />,
  cursor: <FiTerminal className="text-indigo-600" />,
  n8n: <FiZap className="text-orange-500" />,
  chatgpt: <FiCpu className="text-emerald-600" />,
  claude: <FiBox className="text-amber-700" />,
  antigravity: <FiCpu className="text-violet-600" />,
};

const SectionHeader = ({ step, eyebrow, title, description, align = "left", className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.45 }}
    className={`mb-8 md:mb-10 lg:mb-12 ${align === "center" ? "text-center" : ""} ${className}`.trim()}
  >
    <div
      className={`flex items-center gap-3 mb-4 ${
        align === "center" ? "justify-center flex-wrap" : ""
      }`}
    >
      <span className="font-mono text-xs font-medium text-primary-600 dark:text-primary-400 tabular-nums">
        {step}
      </span>
      <span className="h-px w-8 bg-gray-300 dark:bg-gray-600" />
      <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
        {eyebrow}
      </span>
    </div>
    <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-gray-900 dark:text-gray-50">
      {title}
    </h2>
    {description && (
      <p
        className={`mt-3 text-gray-600 dark:text-gray-400 leading-relaxed max-w-2xl text-[15px] md:text-base ${
          align === "center" ? "mx-auto" : ""
        }`}
      >
        {description}
      </p>
    )}
  </motion.div>
);

/** Types one string out; cleans up timers on unmount or when `fullText` changes. */
function useTypingText(fullText, { startDelay = 500, charIntervalMs = 42 } = {}) {
  const [display, setDisplay] = useState("");
  const [complete, setComplete] = useState(false);
  const timersRef = useRef([]);

  useEffect(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
    if (!fullText) {
      setDisplay("");
      setComplete(true);
      return;
    }
    setDisplay("");
    setComplete(false);

    const schedule = (fn, ms) => {
      const id = window.setTimeout(fn, ms);
      timersRef.current.push(id);
      return id;
    };

    let i = 0;
    schedule(() => {
      const tick = () => {
        i += 1;
        const next = fullText.slice(0, i);
        setDisplay(next);
        if (i < fullText.length) {
          schedule(tick, charIntervalMs);
        } else {
          setComplete(true);
        }
      };
      tick();
    }, startDelay);

    return () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };
  }, [fullText, startDelay, charIntervalMs]);

  return { display, complete };
}

const ROLE_TYPING_OPTIONS = { startDelay: 550, charIntervalMs: 38 };

const Home = () => {
  const [profile, setProfile] = useState(null);
  const [projects, setProjects] = useState([]);
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [profileRes, projectsRes, skillsRes] = await Promise.all([
          profileAPI.getProfile().catch(() => ({ data: null })),
          projectsAPI.getFeaturedProjects().catch(() => ({ data: [] })),
          skillsAPI.getSkills({ limit: 100 }).catch(() => ({ data: [] })),
        ]);
        setProfile(profileRes?.data ?? null);
        const rawProjects = projectsRes?.data;
        const rawSkills = skillsRes?.data;
        setProjects(Array.isArray(rawProjects) ? rawProjects : []);
        setSkills(Array.isArray(rawSkills) ? rawSkills : []);
      } catch (error) {
        console.error("Error fetching home data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const roleFullText = profile?.title?.trim() || "Full Stack Developer";
  const { display: typedRole } = useTypingText(
    loading ? "" : roleFullText,
    ROLE_TYPING_OPTIONS,
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <LoadingSpinner size="lg" text="Loading..." />
      </div>
    );
  }

  const projectList = Array.isArray(projects) ? projects : [];
  const skillList = Array.isArray(skills) ? skills : [];
  const featuredProjects = projectList.slice(0, 6);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-950 dark:to-black text-gray-800 dark:text-gray-200">
      {/* ── Hero ── */}
      <section className="relative overflow-x-clip border-b border-gray-200/80 dark:border-gray-800/80 min-h-screen min-h-[100dvh] flex flex-col pt-16 box-border">
        <div
          className="absolute inset-0 text-gray-900 dark:text-gray-100 opacity-[0.04] dark:opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)",
            backgroundSize: "36px 36px",
            maskImage:
              "radial-gradient(ellipse 75% 65% at 50% 40%, black, transparent)",
            WebkitMaskImage:
              "radial-gradient(ellipse 75% 65% at 50% 40%, black, transparent)",
          }}
        />
        <div className="absolute -top-40 -left-40 w-[25rem] sm:w-[30rem] h-[25rem] sm:h-[30rem] bg-primary-400/25 dark:bg-primary-600/15 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 -right-40 w-[22rem] sm:w-[28rem] h-[22rem] sm:h-[28rem] bg-blue-400/25 dark:bg-blue-600/15 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 flex-1 flex flex-col justify-center w-full max-w-7xl mx-auto px-5 sm:px-6 lg:px-16 py-10 sm:py-12 md:py-14 lg:py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8 sm:gap-9 md:gap-10 lg:gap-12 xl:gap-14 items-start lg:items-center lg:pb-0 w-full">
            <div className="order-2 lg:order-none lg:col-span-7 space-y-5 sm:space-y-6 md:space-y-6 lg:space-y-7 text-center md:text-left w-full min-w-0 px-1 sm:px-0 lg:max-w-none">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45 }}
                className="hidden lg:block space-y-4"
              >
                <p className="font-mono text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                  <span className="text-primary-600 dark:text-primary-400">~/</span>
                  portfolio
                  <span className="text-gray-400 dark:text-gray-600"> · </span>
                  <span className="text-gray-600 dark:text-gray-500">README</span>
                </p>
                <div className="inline-flex items-center gap-2 rounded-full border border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-900/50 backdrop-blur-sm px-3.5 py-1.5 mx-auto lg:mx-0">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75 animate-ping" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-primary-500" />
                  </span>
                  <span className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-gray-700 dark:text-gray-200">
                    Available for new projects
                  </span>
                </div>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.05 }}
                className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.08] tracking-tight text-gray-900 dark:text-white"
              >
                Hi, I’m{" "}
                <span className="bg-gradient-to-r from-primary-500 to-blue-500 bg-clip-text text-transparent">
                  {profile?.name || "Shihab Uddin"}
                </span>
              </motion.h1>

              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: 0.12 }}
                className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/90 dark:bg-gray-900/50 px-4 py-3.5 font-mono text-base md:text-lg text-left mx-auto md:mx-0 max-w-xl md:max-w-none lg:max-w-xl min-h-[3.25rem] flex flex-wrap items-center gap-x-0 gap-y-1"
                role="status"
                aria-label={`Role: ${roleFullText}`}
              >
                <span className="text-primary-600 dark:text-primary-400 mr-2 select-none shrink-0">$</span>
                <span className="text-gray-800 dark:text-gray-200 font-medium break-words">
                  {typedRole}
                  <span
                    className="inline-block w-0.5 h-[1.05em] ml-0.5 bg-primary-500 dark:bg-primary-400 animate-pulse align-bottom"
                    aria-hidden="true"
                  />
                </span>
              </motion.div>

              <motion.p
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: 0.2 }}
                className="text-base sm:text-lg text-gray-600 dark:text-gray-400 leading-relaxed max-w-xl md:max-w-none lg:max-w-xl mx-auto md:mx-0"
              >
                {profile?.detailbio ||
                  "Crafting premium web experiences with modern, elegant, and professional design."}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: 0.28 }}
                className="flex flex-wrap items-center justify-center md:justify-start lg:justify-start gap-3 pt-1"
              >
                <Link
                  to="/portfolio"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-500 to-blue-500 text-white rounded-xl font-semibold shadow-lg hover:opacity-95 transition-opacity"
                >
                  View portfolio
                  <FiArrowUpRight size={18} />
                </Link>
                {profile?.resume && (
                  <a
                    href={getImageUrl(profile.resume.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-xl font-semibold border border-gray-200 dark:border-gray-700 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  >
                    <FiDownload size={18} /> Résumé
                  </a>
                )}
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: 0.34 }}
                className="flex flex-wrap items-center justify-center md:justify-start lg:justify-start gap-3 pt-2"
              >
                {profile?.social?.github && (
                  <a
                    href={profile.social.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="GitHub"
                    className="p-3 rounded-xl bg-gray-100 dark:bg-gray-800 hover:bg-primary-500 hover:text-white transition-colors border border-transparent hover:border-primary-600/30"
                  >
                    <FiGithub size={22} />
                  </a>
                )}
                {profile?.social?.linkedin && (
                  <a
                    href={profile.social.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="LinkedIn"
                    className="p-3 rounded-xl bg-gray-100 dark:bg-gray-800 hover:bg-primary-500 hover:text-white transition-colors border border-transparent hover:border-primary-600/30"
                  >
                    <FiLinkedin size={22} />
                  </a>
                )}
                {profile?.social?.email && (
                  <a
                    href={`mailto:${profile.social.email}`}
                    aria-label="Email"
                    className="p-3 rounded-xl bg-gray-100 dark:bg-gray-800 hover:bg-primary-500 hover:text-white transition-colors border border-transparent hover:border-primary-600/30"
                  >
                    <FiMail size={22} />
                  </a>
                )}
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.45, delay: 0.4 }}
                className="flex flex-wrap justify-center md:justify-start lg:justify-start gap-6 pt-6 border-t border-gray-200/80 dark:border-gray-800/80 font-mono text-xs uppercase tracking-[0.12em] text-gray-500 dark:text-gray-400"
              >
                <span>
                  <strong className="text-primary-600 dark:text-primary-400 text-lg tracking-normal normal-case mr-1">
                    {projectList.length}
                  </strong>
                  projects
                </span>
                <span className="hidden sm:inline text-gray-300 dark:text-gray-700">|</span>
                <span>
                  <strong className="text-primary-600 dark:text-primary-400 text-lg tracking-normal normal-case mr-1">
                    {skillList.length}
                  </strong>
                  skills
                </span>
              </motion.div>
            </div>

            <div className="order-1 lg:order-none lg:col-span-5 flex justify-center lg:justify-end shrink-0 w-full min-w-0">
              {profile?.avatar ? (
                <motion.figure
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.08 }}
                  className="
                    flex flex-col w-[min(100%,18rem)]
                    sm:w-[min(100%,21rem)] md:w-[min(100%,22.5rem)] lg:w-[min(100%,23.5rem)]
                    max-w-full mx-auto lg:mx-0
                    rounded-2xl overflow-hidden
                    bg-white dark:bg-[#161b22]
                    border border-black/[0.07] dark:border-white/[0.09]
                    shadow-[0_12px_40px_-14px_rgba(15,23,42,0.5)] dark:shadow-[0_24px_55px_-18px_rgba(0,0,0,0.78)]
                  "
                >
                  {/* Title bar */}
                  <div className="flex items-center gap-2.5 shrink-0 h-9 sm:h-10 px-3 border-b border-black/[0.07] dark:border-white/[0.07] bg-[#eceef2] dark:bg-[#2d333b]">
                    <span className="flex gap-1.5 shrink-0" aria-hidden="true">
                      <span className="h-2 w-2 sm:h-2.5 sm:w-2.5 rounded-full bg-[#ff5f57] shadow-[inset_0_-1px_0_rgba(0,0,0,0.25)]" />
                      <span className="h-2 w-2 sm:h-2.5 sm:w-2.5 rounded-full bg-[#febc2e] shadow-[inset_0_-1px_0_rgba(0,0,0,0.2)]" />
                      <span className="h-2 w-2 sm:h-2.5 sm:w-2.5 rounded-full bg-[#28c840] shadow-[inset_0_-1px_0_rgba(0,0,0,0.2)]" />
                    </span>
                    <div className="min-w-0 flex-1 flex justify-end">
                      <div className="inline-flex max-w-full rounded-md border border-black/[0.08] dark:border-white/[0.1] bg-white dark:bg-[#0d1117] px-2 py-0.5 sm:py-1 shadow-sm">
                        <span className="font-mono text-[9px] sm:text-[10px] text-gray-600 dark:text-gray-400 truncate tabular-nums">
                          <span className="text-gray-400 dark:text-gray-500">~/public/</span>
                          <span className="text-gray-800 dark:text-gray-100">
                            profile<span className="text-primary-600 dark:text-primary-400">.png</span>
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Image viewport — cover + base scale for a tighter “zoomed” crop; card size unchanged */}
                  <div className="p-1.5 sm:p-2 bg-[#f6f8fa] dark:bg-[#0d1117]">
                    <div
                      className="
                        relative w-full overflow-hidden rounded-lg
                        border border-black/[0.06] dark:border-white/[0.08]
                        bg-[#dfe3e8] dark:bg-[#010409]
                        shadow-[inset_0_1px_3px_rgba(0,0,0,0.08)] dark:shadow-[inset_0_2px_6px_rgba(0,0,0,0.6)]
                        aspect-[5/6] lg:aspect-square min-h-[12rem] sm:min-h-[13.5rem] lg:min-h-0 group
                      "
                    >
                      <img
                        src={getImageUrl(profile.avatar.url)}
                        alt={profile.name}
                        className="
                          absolute inset-0 h-full w-full object-cover object-center
                          scale-[1.12] origin-center
                          transition-[transform] duration-[480ms] ease-out
                          group-hover:scale-[1.22] lg:group-hover:scale-[1.26]
                          motion-reduce:scale-100 motion-reduce:transition-none
                          motion-reduce:group-hover:scale-100
                        "
                      />

                      <div className="pointer-events-none absolute inset-x-0 top-0 h-7 sm:h-8 bg-gradient-to-b from-black/[0.05] to-transparent dark:from-white/[0.05]" />

                      <div className="lg:hidden absolute inset-x-0 bottom-0 pointer-events-none">
                        <div className="absolute inset-x-0 bottom-0 h-[42%] max-h-[7.5rem] bg-gradient-to-t from-black/88 via-black/38 to-transparent" />
                        <div className="relative flex flex-wrap items-center gap-1.5 p-2.5 sm:p-3 pb-3">
                          <span className="font-mono text-[10px] sm:text-[11px] text-white/95 rounded-md px-2 py-1 bg-black/48 backdrop-blur-md border border-white/10 shadow-sm tabular-nums">
                            <span className="text-primary-400">●</span> portfolio/active
                          </span>
                          <span className="inline-flex items-center gap-1.5 font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.12em] text-white/94 rounded-full pl-2 pr-2 py-1 bg-black/42 backdrop-blur-md border border-white/10 shadow-sm">
                            <span className="relative flex h-1.5 w-1.5">
                              <span className="absolute inset-0 animate-ping rounded-full bg-primary-400 opacity-60" />
                              <span className="relative rounded-full h-1.5 w-1.5 bg-primary-500 shrink-0" />
                            </span>
                            Available
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <figcaption className="border-t border-black/[0.07] dark:border-white/[0.07] bg-[#f6f8fa] dark:bg-[#0d1117] text-left">
                    <div className="flex items-center justify-between gap-2 px-2.5 sm:px-3 py-1.5 border-b border-black/[0.05] dark:border-white/[0.05] font-mono text-[9px] sm:text-[10px] text-gray-500 dark:text-gray-500 uppercase tracking-[0.08em]">
                      <span className="opacity-90">Workspace</span>
                      <span className="tabular-nums opacity-90 truncate max-w-[55%]">PNG · synced</span>
                    </div>
                    <div className="hidden lg:block px-3 py-2.5 font-mono text-[11px] leading-relaxed bg-[#0c0c0c] dark:bg-black text-gray-400 dark:text-gray-500 border-t border-white/[0.06] dark:border-white/[0.05]">
                      <div>
                        <span className="text-primary-500 dark:text-primary-400 select-none">$</span>
                        <span className="text-gray-600 dark:text-gray-500"> whoami</span>
                      </div>
                      <div className="text-blue-700 dark:text-blue-300 truncate font-medium mt-0.5">
                        {(profile?.title || "developer").toLowerCase().replace(/\s+/g, "_")}
                      </div>
                      <div className="mt-1 inline-flex items-center">
                        <span className="text-primary-500 dark:text-primary-400 select-none">$</span>
                        <span className="ml-2 inline-block h-3 w-1.5 rounded-[1px] bg-gray-500 dark:bg-gray-600 animate-pulse motion-reduce:animate-none" />
                      </div>
                    </div>
                    <p className="lg:hidden px-2.5 sm:px-3 py-2 font-mono text-[10px] sm:text-[11px] leading-snug text-gray-700 dark:text-gray-400 tracking-tight truncate">
                      <span className="text-primary-600 dark:text-primary-400">$</span> ready ·{" "}
                      <span className="text-gray-600 dark:text-gray-500 truncate">
                        {(profile?.title || "developer").toLowerCase().replace(/\s+/g, "_")}
                      </span>
                    </p>
                  </figcaption>
                </motion.figure>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="w-full max-w-md mx-auto lg:mx-0 lg:ml-auto rounded-xl overflow-hidden border border-gray-200/90 dark:border-gray-700/90 bg-white dark:bg-gray-950 shadow-xl ring-1 ring-gray-950/[0.04] dark:ring-white/[0.06]"
                >
                  <div className="flex items-center gap-3 h-10 px-3 border-b border-gray-200 dark:border-gray-800 bg-gray-50/95 dark:bg-gray-900/95">
                    <span className="flex gap-1.5 shrink-0" aria-hidden="true">
                      <span className="h-2.5 w-2.5 rounded-full bg-[#FF5F57]/90" />
                      <span className="h-2.5 w-2.5 rounded-full bg-[#FEBC2E]/90" />
                      <span className="h-2.5 w-2.5 rounded-full bg-[#28C840]/90" />
                    </span>
                    <span className="flex-1 text-center font-mono text-[10px] text-gray-500 truncate">src/pages/index.tsx</span>
                  </div>
                  <div className="lg:hidden px-3 py-3 border-b border-gray-200/80 dark:border-gray-800/80 bg-gray-50/60 dark:bg-gray-900/40">
                    <p className="font-mono text-[11px] text-gray-600 dark:text-gray-400 text-center sm:text-left">
                      <span className="text-primary-600 dark:text-primary-400">~/</span>
                      portfolio<span className="text-gray-400 dark:text-gray-600"> · </span>
                      <span className="text-gray-500">README</span>
                    </p>
                    <div className="mt-2 inline-flex items-center gap-2 rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/70 px-2.5 py-1 mx-auto sm:mx-0">
                      <span className="relative flex h-1.5 w-1.5">
                        <span className="absolute rounded-full inset-0 bg-primary-400/70 animate-ping" />
                        <span className="relative rounded-full h-1.5 w-1.5 bg-primary-500" />
                      </span>
                      <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-gray-700 dark:text-gray-200">
                        Available for projects
                      </span>
                    </div>
                  </div>
                  <pre className="p-4 sm:p-5 font-mono text-[11px] sm:text-xs leading-relaxed text-gray-700 dark:text-gray-400 bg-gray-50/50 dark:bg-[#0d1117]/60 text-left overflow-x-auto border-t border-transparent">
                    <span className="text-gray-400 dark:text-gray-600">{"// "}intro</span>
                    {"\n"}
                    <span className="text-primary-600 dark:text-primary-400">const</span> role ={" "}
                    {JSON.stringify(profile?.title || "Full Stack Developer")};{"\n"}
                    <span className="text-primary-600 dark:text-primary-400">export default</span> role;
                  </pre>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </section>

      {projectList.length > 0 && (
        <section className="relative min-h-screen min-h-[100dvh] flex flex-col pt-16 box-border border-b border-gray-200/70 dark:border-gray-800/80 overflow-x-clip bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-black">
          <div className="flex-1 flex flex-col justify-center w-full max-w-7xl mx-auto px-5 sm:px-6 lg:px-16 py-10 sm:py-12 md:py-14">
            <SectionHeader
              step="02"
              eyebrow="Selected work"
              title="Featured projects"
              description="Production apps, internal tools, and experiments — concise case studies below."
            />
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45 }}
              className="grid grid-cols-1 items-stretch gap-5 sm:grid-cols-2 sm:gap-6 lg:gap-7 xl:grid-cols-3 xl:gap-8"
            >
              {featuredProjects.map((project) => (
                <ProjectCard key={project._id} project={project} />
              ))}
            </motion.div>
            {projectList.length > featuredProjects.length && (
              <p className="mt-6 text-center font-mono text-xs text-gray-500 dark:text-gray-500">
                Showing {featuredProjects.length} of {projectList.length} featured — open the portfolio for the full list.
              </p>
            )}
            <div className="mt-10 md:mt-12 flex justify-center shrink-0">
              <Link
                to="/portfolio"
                className="inline-flex items-center gap-2 font-mono text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 border border-primary-600/35 dark:border-primary-500/35 rounded-xl px-5 py-2.5 hover:bg-primary-500/10 transition-colors"
              >
                Browse all projects
                <FiArrowRight size={16} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {skillList.length > 0 && (
        <section className="relative min-h-screen min-h-[100dvh] flex flex-col pt-16 box-border overflow-x-clip border-b border-gray-200/50 dark:border-gray-900/80">
          <div className="absolute -top-40 -right-40 w-[22rem] h-[22rem] bg-green-400/20 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-40 -left-40 w-[26rem] h-[26rem] bg-indigo-400/20 rounded-full blur-3xl pointer-events-none" />

          <div className="flex-1 flex flex-col justify-center w-full max-w-7xl mx-auto relative z-10 px-5 sm:px-6 lg:px-16 py-10 sm:py-12 md:py-14">
            <SectionHeader
              step="03"
              eyebrow="Stack"
              title="Skills & technologies"
              description="Frontend, backend, data, DevOps, and tooling I rely on to ship production-ready apps."
            />
            {(() => {
              const baseOrder = [
                "frontend",
                "backend",
                "database",
                "devops",
                "tools",
                "other",
              ];
              const labels = {
                frontend: "Frontend",
                backend: "Backend",
                database: "Database",
                devops: "DevOps",
                tools: "Tools",
                other: "Other",
              };

              const grouped = skillList.reduce((acc, s) => {
                const raw = (s.category || "other").toLowerCase().trim();
                const key = raw === "tool" ? "tools" : raw;
                acc[key] = acc[key] || [];
                acc[key].push(s);
                return acc;
              }, {});
              const dynamicOrder = [
                ...baseOrder.filter((cat) => grouped[cat]?.length),
                ...Object.keys(grouped).filter((cat) => !baseOrder.includes(cat)),
              ];

              return (
                <div className="space-y-8 md:space-y-10 lg:space-y-11">
                  {dynamicOrder.map((cat, idx) => (
                    <motion.div
                      key={cat}
                      initial={{ opacity: 0, y: 14 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-40px" }}
                      transition={{ duration: 0.4, delay: idx * 0.03 }}
                      className="space-y-5"
                    >
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-[11px] text-gray-400 dark:text-gray-500 tabular-nums">
                          {(idx + 1).toString().padStart(2, "0")}
                        </span>
                        <h3 className="text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100">
                          {labels[cat] || cat.charAt(0).toUpperCase() + cat.slice(1)}
                        </h3>
                        <span className="font-mono text-xs text-gray-400 dark:text-gray-500">
                          ({grouped[cat].length})
                        </span>
                        <div className="h-px flex-1 bg-gradient-to-r from-gray-300 dark:from-gray-700 to-transparent" />
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
                        {grouped[cat].map((skill, index) => (
                          <motion.div
                            key={skill._id}
                            initial={{ opacity: 0, y: 8 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.015 }}
                            className="group flex items-center gap-3 rounded-2xl border border-gray-200/80 dark:border-gray-700/80 bg-white/70 dark:bg-gray-800/40 backdrop-blur-md px-3.5 py-3 shadow-sm hover:shadow-md hover:border-primary-500/35 dark:hover:border-primary-500/25 transition-all duration-300"
                          >
                            <div className="w-10 h-10 md:w-11 md:h-11 rounded-xl bg-gradient-to-br from-primary-500/12 to-blue-500/12 border border-primary-500/20 dark:border-primary-500/25 flex items-center justify-center flex-shrink-0">
                              <div className="text-xl md:text-2xl leading-none">
                                {skillIconsMap[skill.icon?.toLowerCase()] || (
                                  <FiCode className="text-primary-600 dark:text-primary-400" size={20} />
                                )}
                              </div>
                            </div>
                            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                              {skill.name}
                            </p>
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  ))}
                </div>
              );
            })()}
          </div>
        </section>
      )}
    </div>
  );
};

const ProjectCard = ({ project }) => {
  return (
    <motion.article
      whileHover={{ y: -3 }}
      transition={{ type: "spring", stiffness: 380, damping: 28 }}
      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-gray-200/90 bg-white shadow-md transition-[box-shadow,border-color,transform] hover:border-primary-500/25 hover:shadow-xl dark:border-gray-700 dark:bg-gray-800 dark:hover:border-primary-500/20"
    >
      {project.images?.[0] ? (
        <div className="relative aspect-[16/10] w-full shrink-0 overflow-hidden bg-gray-100 dark:bg-gray-900/60">
          <img
            src={getImageUrl(project.images[0].url)}
            alt={project.title}
            className="h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-[1.03]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-950/55 via-transparent to-transparent opacity-80 pointer-events-none" />
          <div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-300">
            {project.liveUrl && (
              <a
                href={project.liveUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-lg bg-white/90 dark:bg-gray-900/95 text-gray-800 dark:text-gray-100 hover:text-primary-600 dark:hover:text-primary-400 shadow border border-gray-200/80 dark:border-gray-700"
                aria-label="Live site"
              >
                <FiExternalLink size={16} />
              </a>
            )}
            {project.githubUrl && (
              <a
                href={project.githubUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-lg bg-white/90 dark:bg-gray-900/95 text-gray-800 dark:text-gray-100 hover:text-primary-600 dark:hover:text-primary-400 shadow border border-gray-200/80 dark:border-gray-700"
                aria-label="Source code"
              >
                <FiGithub size={16} />
              </a>
            )}
          </div>
        </div>
      ) : (
        <div className="flex aspect-[16/10] w-full shrink-0 items-center justify-center border-b border-gray-200 bg-gradient-to-br from-gray-100 to-gray-200/80 dark:border-gray-700 dark:from-gray-800 dark:to-gray-900">
          <FiCode className="text-gray-300 dark:text-gray-600" size={36} strokeWidth={1.25} />
        </div>
      )}

      <div className="group flex min-h-0 flex-1 flex-col p-5 md:p-6">
        <div className="flex items-center gap-2 mb-2">
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-primary-600 dark:text-primary-400">
            Project
          </span>
          <span className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
        </div>
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 line-clamp-1 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
          {project.title}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 flex-grow line-clamp-3 leading-relaxed">
          {project.description}
        </p>
        {project.technologies && project.technologies.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4">
            {project.technologies.slice(0, 4).map((tech, index) => (
              <span
                key={index}
                className="px-2.5 py-1 font-mono text-[10px] md:text-[11px] tracking-wide rounded-md bg-primary-100 dark:bg-primary-900/25 text-primary-700 dark:text-primary-400 border border-primary-200/60 dark:border-primary-800/50"
              >
                {tech}
              </span>
            ))}
            {project.technologies.length > 4 && (
              <span className="px-2 py-1 font-mono text-[10px] text-gray-500">
                +{project.technologies.length - 4}
              </span>
            )}
          </div>
        )}
        <Link
          to={`/project/${project._id}`}
          className="inline-flex items-center gap-1 mt-5 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-semibold text-sm group/link pt-2 border-t border-gray-100 dark:border-gray-700/80"
        >
          View case study
          <FiArrowRight className="transition-transform group-hover/link:translate-x-0.5" size={16} />
        </Link>
      </div>
    </motion.article>
  );
};

export default Home;
